/* eslint-disable no-param-reassign */
import React, { Fragment } from 'react';
import _ from 'lodash';
import {
  Datagrid,
  Edit,
  FormDataConsumer,
  FormTab,
  TabbedForm,
  ArrayInput,
  BooleanInput,
  ImageInput,
  ImageField,
  ReferenceInput,
  ReferenceManyField,
  SelectInput,
  TextField,
  TextInput,
  LongTextInput,
  UrlField,
  FileInput,
  DisabledInput,
  NumberInput,
  Labeled,
  NumberField,
  CardActions,
  EditButton,
  ListButton,
  Button,
  REDUX_FORM_NAME,
} from 'react-admin';
import { change } from 'redux-form';
import RichTextInput from 'ra-input-rich-text';
import { DateInput } from 'react-admin-date-inputs';
import './styles.css';
import ShowButton from './ShowButton';
import SaveOnlyToolbar from '../SaveOnlyToolbar';
import dateInputOptions from '../../dateInputOptions';
import NoVisibleIndexSimpleFormIterator from '../NoVisibleIndexSimpleFormIterator';
import { offeringCategory, offerType, offerIndustry, offerSecurityType, externalOfferSelection, subscriptionAgreement, securityDeliveryInformationOptionTypes } from '../../helpers/mappings';

import {
  isRequired,
  isPostiveDecimal,
  isNumericRequired,
  isLargerThan0Decimal,
  validateOffer,
  isBooleanRequired,
  validateShortUrl,
} from '../../validators';
import warningImage from '../../assets/images/warning.svg';

const Title = ({ record }) => (
  <span>Offer: <strong>{record.name}</strong></span>
);


const Actions = ({ basePath, data, path }) => (
  <CardActions>
    <ShowButton basePath={basePath} record={data} path={path} />
    <ListButton basePath={basePath} />
  </CardActions>
);

const ImagePreview = ({ record, label }) => (
  <Labeled label={!label ? ' ' : label}>
    <img src={record.type === 'vimeo' ? record.previewImageUrl : record.url} alt="" width="300" />
  </Labeled>
);

const Warning = warningText => (
  <div className="disclaimerInfo">
    <img src={warningImage} alt="info" className="disclaimerImage" />
    <div className="disclaimerText">{ warningText }</div>
  </div>
);

const AccumulatedInvestments = ({ record, formData }) => {
  const createdOfferOrders = record.orders ? record.orders.filter(order => order.status === 'created') : [];
  const createdOfferOrdersAccumulated = createdOfferOrders.map(order => order.requestedTotalCurrencyAmt).reduce((x, y) => x + y, 0);

  const submittedOfferOrders = record.orders ? record.orders.filter(order => order.status === 'submitted') : [];
  const submittedOfferOrdersAccumulated = submittedOfferOrders.map(order => order.requestedTotalCurrencyAmt).reduce((x, y) => x + y, 0);

  const completedOfferOrders = record.orders ? record.orders.filter(order => order.status === 'completed') : [];
  const completedOfferOrdersAccumulated = completedOfferOrders.map(order => order.requestedTotalCurrencyAmt).reduce((x, y) => x + y, 0);

  const rejectedOfferOrders = record.orders ? record.orders.filter(order => order.status === 'rejected') : [];
  const rejectedOfferOrdersAccumulated = rejectedOfferOrders.map(order => order.requestedTotalCurrencyAmt).reduce((x, y) => x + y, 0);

  const cancelledOfferOrders = record.orders ? record.orders.filter(order => order.status === 'cancelled') : [];
  const cancelledOfferOrdersAccumulated = cancelledOfferOrders.map(order => order.requestedTotalCurrencyAmt).reduce((x, y) => x + y, 0);

  const deletedOfferOrders = record.orders ? record.orders.filter(order => order.status === 'deleted') : [];
  const deletedOfferOrdersAccumulated = deletedOfferOrders.map(order => order.requestedTotalCurrencyAmt).reduce((x, y) => x + y, 0);

  let summationOfFunnel = 0;
  if (formData.offerOrderSummation.useCustom) {
    summationOfFunnel += formData.offerOrderSummation.custom;
  } else {
    if (formData.offerOrderSummation.created) {
      summationOfFunnel += createdOfferOrdersAccumulated;
    }
    if (formData.offerOrderSummation.submitted) {
      summationOfFunnel += submittedOfferOrdersAccumulated;
    }
    if (formData.offerOrderSummation.completed) {
      summationOfFunnel += completedOfferOrdersAccumulated;
    }
    if (formData.offerOrderSummation.rejected) {
      summationOfFunnel += rejectedOfferOrdersAccumulated;
    }
    if (formData.offerOrderSummation.cancelled) {
      summationOfFunnel += cancelledOfferOrdersAccumulated;
    }
    if (formData.offerOrderSummation.deleted) {
      summationOfFunnel += deletedOfferOrdersAccumulated;
    }
  }
  return (
    <div style={{ width: '100%' }}>
      <table style={{
        width: '100%',
        fontSize: 14,
        border: '1px solid grey',
        borderRadius: 15,
        padding: 10,
      }}
      >
        <tbody>
          <tr>
            <td colSpan="4" className="reactAdminLabel">Accumulated Investments</td>
          </tr>
          <tr style={{ paddingTop: 10 }}>
            <td style={{ width: '40%' }}>Offer Order Classification</td>
            <td style={{ width: '10%' }}>Count</td>
            <td>Sum of Investments</td>
            <td>Include in Summation</td>
          </tr>
          <tr>
            <td >Created Offer Orders (not submitted to Folio)</td>
            <td>{createdOfferOrders.length}</td>
            <td>{createdOfferOrdersAccumulated.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
            <td>
              <BooleanInput
                source="offerOrderSummation.created"
                record={record}
                label=""
                options={{ disabled: formData.offerOrderSummation.useCustom }}
              />
            </td>
          </tr>
          <tr>
            <td>Submitted Offer Orders (pending from Folio)</td>
            <td>{submittedOfferOrders.length}</td>
            <td>{submittedOfferOrdersAccumulated.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
            <td>
              <BooleanInput
                source="offerOrderSummation.submitted"
                record={record}
                label=""
                options={{ disabled: formData.offerOrderSummation.useCustom }}
              />
            </td>
          </tr>
          <tr>
            <td>Submitted Offer Orders (completed from Folio)</td>
            <td>{completedOfferOrders.length}</td>
            <td>{completedOfferOrdersAccumulated.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
            <td>
              <BooleanInput
                source="offerOrderSummation.completed"
                record={record}
                label=""
                options={{ disabled: formData.offerOrderSummation.useCustom }}
              />
            </td>
          </tr>
          <tr>
            <td>Submitted Offer Orders (rejected from Folio)</td>
            <td>{rejectedOfferOrders.length}</td>
            <td>{rejectedOfferOrdersAccumulated.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
            <td>
              <BooleanInput
                source="offerOrderSummation.rejected"
                record={record}
                label=""
                options={{ disabled: formData.offerOrderSummation.useCustom }}
              />
            </td>
          </tr>
          <tr>
            <td>Submitted Offer Orders (cancelled from Folio)</td>
            <td>{cancelledOfferOrders.length}</td>
            <td>{cancelledOfferOrdersAccumulated.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
            <td>
              <BooleanInput
                source="offerOrderSummation.cancelled"
                record={record}
                label=""
                options={{ disabled: formData.offerOrderSummation.useCustom }}
              />
            </td>
          </tr>
          <tr>
            <td>Cancelled Offer Orders (from Admin or User)</td>
            <td>{deletedOfferOrders.length}</td>
            <td>{deletedOfferOrdersAccumulated.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</td>
            <td>
              <BooleanInput
                source="offerOrderSummation.deleted"
                record={record}
                label=""
                options={{ disabled: formData.offerOrderSummation.useCustom }}
              />
            </td>
          </tr>
          <tr>
            <td colSpan="3">
              <NumberInput
                source="offerOrderSummation.custom"
                style={{ width: '100%' }}
                label="Custom Accumulated Value"
                disabled={!formData.offerOrderSummation || !(formData.offerOrderSummation && formData.offerOrderSummation.useCustom)}
              />
            </td>
            <td>
              <BooleanInput
                source="offerOrderSummation.useCustom"
                record={record}
                label=""
                onChange={(e) => {
                  const val = e.target.checked;
                  if (val) {
                    formData.offerOrderSummation.created = false;
                    formData.offerOrderSummation.submitted = false;
                    formData.offerOrderSummation.deleted = false;
                    formData.offerOrderSummation.completed = false;
                    formData.offerOrderSummation.rejected = false;
                    formData.offerOrderSummation.cancelled = false;
                  } else {
                    formData.offerOrderSummation.custom = null;
                  }
                }}
              />
            </td>
          </tr>
          <tr style={{ paddingTop: 10 }}>
            <td colSpan="2">
              Summation that will be displayed in Funnel:
            </td>
            <td colSpan="2">
              {summationOfFunnel.toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default props => (
  <Edit
    {...props}
    title={<Title />}
    actions={<Actions path={props.location.pathname} />}
    undoable={false}
  >
    <TabbedForm toolbar={<SaveOnlyToolbar />} validate={validateOffer}>
      <FormTab label="Info">
        <TextField source="id" />
        <TextInput source="shortUrlRoute" label="Short URL" validate={validateShortUrl} />
        <ImageInput source="_logoUrl" label="Logo" accept="image/*">
          <ImageField source="src" title="title" />
        </ImageInput>

        <TextInput source="name" validate={isRequired} />

        <SelectInput
          source="category"
          choices={_.values(offeringCategory).map(item => ({
              label: item.label,
              id: item.value,
            }))}
          optionText="label"
          validate={isNumericRequired}
        />
        <SelectInput
          label="Offer source"
          source="isExternalOffer"
          choices={_.values(externalOfferSelection).map(item => ({
              label: item.label,
              id: item.value,
            }))}
          optionText="label"
          validate={isBooleanRequired}
        />
        <FormDataConsumer>
          {({ formData }) => (formData.isExternalOffer !== externalOfferSelection.EXTERNAL_OFFER.value && (
          <ReferenceInput
            label="Folio Private Placement Offer"
            source="folioId"
            reference="folioPrivatePlacementOffers"
            allowEmpty
            perPage={100}
          >
            <SelectInput
              optionText={choice => `${choice.offerName} (${choice.offerStatus} - ${choice.privatePlacementOfferOid})`}
            />
          </ReferenceInput>
            ))}
        </FormDataConsumer>
        <FormDataConsumer>
          {({ formData }) => (formData.isExternalOffer === externalOfferSelection.EXTERNAL_OFFER.value && (
            <SelectInput
              label="Subscription Agreement"
              source="subscriptionAgreement"
              choices={_.values(subscriptionAgreement).map(item => ({
              label: item.label,
              id: item.value,
            }))}
              optionText="label"
              style={{ width: 300 }}
            />
          ))}
        </FormDataConsumer>
        <FormDataConsumer>
          {({ formData }) => (!_.isEmpty(formData) && formData.isExternalOffer === externalOfferSelection.EXTERNAL_OFFER.value && (
          <Fragment>
            <table style={{ width: '100%' }}>
              <tbody>
                <tr>
                  <td colSpan="2" style={{ color: 'rgba(0, 0, 0, 0.54)', paddingTop: 10 }}>Wiring Instructions: (found below, only for Escrow Offers)</td>
                </tr>
                <tr>
                  <td>
                    <TextInput
                      source="wiringInstrBankName"
                      validate={isRequired}
                      label="Bank Name"
                      style={{ width: '95%' }}
                    />
                  </td>
                  <td>
                    <TextInput
                      source="wiringInstrABANumber"
                      validate={isRequired}
                      label="ABA #"
                      style={{ width: '95%' }}
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <TextInput
                      source="wiringInstrAccountNumber"
                      validate={isRequired}
                      label="A/C #"
                      style={{ width: '95%' }}
                    />
                  </td>
                  <td>
                    <TextInput
                      source="wiringInstrAccountName"
                      validate={isRequired}
                      label="A/C Name"
                      style={{ width: '95%' }}
                    />
                  </td>
                </tr>
                <tr>
                  <td colSpan="2">
                    <TextInput
                      source="wiringInstrAttentionLine"
                      validate={isRequired}
                      label="Attention Line"
                      style={{ width: '100%' }}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </Fragment>
        ))}
        </FormDataConsumer>
        <FormDataConsumer>
          {({ formData }) => (!_.isEmpty(formData) && formData.isExternalOffer === externalOfferSelection.EXTERNAL_OFFER.value &&
          <table style={{ width: '100%' }}>
            <tbody>
              <tr>
                <td style={{ width: '99%' }}>
                  <SelectInput
                    source="securityDeliveryInformationOptions.rootType"
                    choices={_.values(securityDeliveryInformationOptionTypes).map(item => ({
                    label: item.label,
                    id: item.value,
                  }))}
                    optionText="label"
                    label="Security Delivery Information Options Type"
                    fullWidth
                  />
                </td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <FormDataConsumer>
                    {({ dispatch }) => (
                      <Button
                        style={{ marginTop: 20 }}
                        onClick={() => {
                          dispatch(change(
                            REDUX_FORM_NAME,
                            'securityDeliveryInformationOptions.options',
                            [
                              {
                                hasTooltip: true,
                                isReadOnly: false,
                                optionLabel: 'Retain at the <tooltip>Transfer Agent</tooltip> (Securities held in electronic form)',
                                initialValue: true,
                                optionTooltip: 'Public companies typically use transfer agents to keep track of the individuals and entities that own their securities.' +
                                  'Transfer agents keep records of who owns a company\'s securities and how those securities are held—whether by the owner in certificate form' +
                                  ', by the company in book-entry form (electronic form), or by the investor\'s brokerage firm in street name. They also keep records ' +
                                  'of how many shares each investor owns.',
                              },
                              {
                                optionLabel: 'Deliver to my address of record (Physical securities will be mailed to you)',
                                initialValue: false,
                              },
                            ],
                          ));
                          dispatch(change(
                            REDUX_FORM_NAME,
                            'securityDeliveryInformationOptions.rootType',
                            securityDeliveryInformationOptionTypes.RADIO_LIST.value,
                          ));
                          dispatch(change(
                            REDUX_FORM_NAME,
                            'securityDeliveryInformationOptions.disclaimer',
                            'This Offering requires you to wire funds directly from an existing bank account to the Offering Escrow Account. ' +
                            'You will receive an email with instructions on how to complete your purchase. For this offering type, If you have' +
                            ' funds in your My IPO account they cannot be transferred to the Offering Escrow Account. The securities purchased ' +
                            'can be retained in electronic form at the transfer agent for the Issuer or can be delivered to you in physical form.' +
                            ' Some brokerage firms may not hold these securities or may charge fees to deposit these securities.',
                          ));
                        }}
                        label="Add default Security Delivery Information Options"
                      />)}
                  </FormDataConsumer>
                </td>
              </tr>
              <tr>
                <td colSpan="2">
                  <TextInput source="securityDeliveryInformationOptions.disclaimer" label="Escrow Disclaimer" fullWidth multiline minRows={4} />
                </td>
              </tr>
            </tbody>
          </table>)}
        </FormDataConsumer>
        <FormDataConsumer>
          {({ formData }) => (!_.isEmpty(formData) && formData.isExternalOffer === externalOfferSelection.EXTERNAL_OFFER.value &&
          <ArrayInput source="securityDeliveryInformationOptions.options" label="Security Delivery Information Options" fullWidth>
            <NoVisibleIndexSimpleFormIterator>
              <TextInput source="optionLabel" label="Option Label" fullWidth />
              <BooleanInput source="isReadOnly" label="Is Read Only" fullWidth />
              <BooleanInput source="initialValue" label="Initial Value (Checked or Unchecked)" fullWidth />
              <BooleanInput source="hasTooltip" label="Has Tooltip" fullWidth />
              <TextInput source="optionTooltip" label="Tooltip text" fullWidth multiline minRows={2} />
            </NoVisibleIndexSimpleFormIterator>
          </ArrayInput>)}
        </FormDataConsumer>
        <ArrayInput source="nonVisibleAppFields" label="Fields not visible in the Apps">
          <NoVisibleIndexSimpleFormIterator>
            <SelectInput
              source="fieldName"
              label="Field Name"
              choices={[
                { id: 'yield', label: 'Yield' },
                { id: 'yieldDescription', label: 'Yield Description' },
              ]}
              optionText="label"
            />
          </NoVisibleIndexSimpleFormIterator>
        </ArrayInput>
        <FormDataConsumer>
          {({ formData }) => (<SelectInput
            source="status"
            choices={[
              { id: 'active' },
              { id: 'closed' },
            ]}
            optionText="id"
            validate={isRequired}
            onChange={() => {
              formData.offerOrderSummation.created = false;
              formData.offerOrderSummation.submitted = false;
              formData.offerOrderSummation.deleted = false;
              formData.offerOrderSummation.completed = false;
              formData.offerOrderSummation.rejected = false;
              formData.offerOrderSummation.cancelled = false;
              formData.offerOrderSummation.useCustom = false;
              formData.offerOrderSummation.custom = null;
          }}
          />)}
        </FormDataConsumer>
        <FormDataConsumer>
          {({ formData, ...rest }) => formData.status === 'closed' && <AccumulatedInvestments formData={formData} {...rest} />}
        </FormDataConsumer>

        <DateInput
          source="startDate"
          label="Start date"
          options={dateInputOptions}
        />

        <BooleanInput source="recurring" />

        <FormDataConsumer>
          {({ formData }) => (formData.recurring ? (
            <ArrayInput source="closeDates">
              <NoVisibleIndexSimpleFormIterator>
                <DateInput source="date" label="Date" options={{ ...dateInputOptions, minDate: formData.startDate }} />
              </NoVisibleIndexSimpleFormIterator>
            </ArrayInput>
            ) : <TextInput source="closeDate" label="Closing date" />)
          }
        </FormDataConsumer>
        <TextInput source="anticipatedStartDate" />
        <ArrayInput source="investmentRanges">
          <NoVisibleIndexSimpleFormIterator>
            <TextInput source="investmentRange" />
          </NoVisibleIndexSimpleFormIterator>
        </ArrayInput>
        <BooleanInput source="featured" />
        {Warning('Toggling on the above Featured option and then saving your changes, will replace the current Featured Offer with this offer in the funnel.')}
        <BooleanInput source="new" />
        <BooleanInput source="visibleToEndUsers" />
        <SelectInput
          source="offerType"
          choices={_.values(offerType).map(item => ({
              label: item.label,
              id: item.value,
            }))}
          optionText="label"
          label="Offer Type"
        />
        <SelectInput
          source="industry"
          choices={_.values(offerIndustry).map(item => ({
              label: item.label,
              id: item.value,
            }))}
          optionText="label"
          label="Offering Industry"
        />
        <TextInput source="sector" />
        <SelectInput
          source="securityType"
          choices={_.values(offerSecurityType).map(item => ({
              label: item.label,
              id: item.value,
            }))}
          optionText="label"
          label="Security Type"
        />
        <TextInput source="yield" />
        <LongTextInput source="yieldDescription" />
        <RichTextInput source="exitDescription" label="Exit Strategy/Liquidity" />
        <TextInput source="subTextOnClosed" label="Subtext On Closed Offer" fullWidth multiline minRows="1" maxRows="4" />
      </FormTab>

      <FormTab label="Assets">
        <ArrayInput source="assets">
          <NoVisibleIndexSimpleFormIterator disableAdd>
            <ImagePreview label="Preview" />
            <DisabledInput source="type" />
            <DisabledInput source="url" label="Link" />
          </NoVisibleIndexSimpleFormIterator>
        </ArrayInput>
        <ArrayInput source="_assets" label=" ">
          <NoVisibleIndexSimpleFormIterator>
            <BooleanInput source="isVideo" label="Video" />
            <FormDataConsumer>
              {({
                  formData, // Entire form Data
                  scopedFormData, // Form data for this item
                  getSource, // Get valid source inside arrayInput
                  ...rest
                }) => scopedFormData && scopedFormData.isVideo &&
                  <TextInput {...rest} source={getSource('vimeoUrl')} label="Vimeo Url" validate={isRequired} />
                }
            </FormDataConsumer>
            <FileInput source="_asset" label="Image/Thumbnail" accept="image/*" validate={file => (!file ? 'Missing file' : undefined)}>
              <ImageField source="src" title="title" />
            </FileInput>
          </NoVisibleIndexSimpleFormIterator>
        </ArrayInput>
      </FormTab>

      <FormTab label="Price & Limits">
        <NumberInput label="Minimum Raise Amount" source="minTotalRaiseCurrencyAmt" />
        <NumberInput label="Maximum Raise Amount" source="maxTotalRaiseCurrencyAmt" />
        <NumberField label="Current Amount Raised" source="totalRaisedAmount" options={{ style: 'currency', currency: 'USD' }} />

        <FormDataConsumer>
          {({ formData, ...rest }) => (formData.category !== offeringCategory.CONDITIONAL.value ?
            <NumberInput label="Price Per Share" source="pricePerShare" {...rest} /> : null)
            }
        </FormDataConsumer>
        <NumberInput label="Minimum Investment Increment" source="minimumInvestIncCurrencyAmt" />

        <NumberInput label="Minimum Investment Currency" source="minimumInvestCurrencyAmt" />
        <NumberInput label="Maximum Investment Currency" source="maximumInvestCurrencyAmt" />

        <BooleanInput source="allowRestrictedPersons" />
        {/* Conditional Offer */}
        <FormDataConsumer>
          {({ formData, ...rest }) => (formData.category === offeringCategory.CONDITIONAL.value ?
            <div>
              <br />
              <h3>Conditional Offer Details</h3>
              <div>
                <NumberInput label="Price Per Share" source="pricePerShare" validate={isPostiveDecimal} {...rest} />
              </div>
              <div>
                <NumberInput validate={isLargerThan0Decimal} label="Min Share Price" source="minSharePrice" {...rest} />
              </div>
              <div>
                <NumberInput validate={isLargerThan0Decimal} label="Max Share Price" source="maxSharePrice" {...rest} />
              </div>
            </div> : null
            )}
        </FormDataConsumer>
      </FormTab>

      <FormTab label="Description">
        <RichTextInput source="description" />
      </FormTab>

      <FormTab label="Disclaimer">
        <RichTextInput source="disclaimer" />
      </FormTab>
      <FormTab label="Documents">
        <ReferenceManyField label="Documents" reference="offeringDocuments" target="offerId">
          <Datagrid>
            <TextField source="docType" />
            <UrlField source="url" target="_blank" />
            <EditButton />
          </Datagrid>
        </ReferenceManyField>
      </FormTab>
    </TabbedForm>
  </Edit>
);




// WEBPACK FOOTER //
// ./src/components/Offer/Edit.js